import React, { ButtonHTMLAttributes, ReactElement } from 'react';

import { css, Interpolation, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { SubmitButton } from '@hangar31/rc-form';
import toColor from '@OnArk/utils/toColor';

interface ButtonColorProps {
  negative?: boolean;
  white?: boolean;
  outline?: boolean;
}

interface BaseButtonProps {
  withIcon?: boolean | 'right' | 'sm' | 'only';
  bottom?: string;
  top?: string;
  left?: string;
  right?: string;
  inline?: boolean;
  target?: string;
  htmlFor?: string;
  href?: string;
}

const BaseButtonStyles: Interpolation<{ theme: Theme } & BaseButtonProps> = [
  ({ theme }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
    padding: 0.9rem 2.7rem;

    line-height: 1em;

    text-align: center;
    vertical-align: middle;

    border-radius: ${theme.radius.md};

    &:hover {
      opacity: 0.6;
    }
  `,
  ({ theme }) => theme.font('xs'),
];
const ButtonColorStyles: Interpolation<{ theme: Theme } & ButtonColorProps> = ({
  negative,
  outline,
  theme,
  white,
}) => {
  let secondaryColor = theme.colors.white;
  let primaryColor = theme.colors.clickable;

  if (negative) {
    secondaryColor = theme.colors.white;
    primaryColor = theme.colors.highlight;
  } else if (white) {
    secondaryColor = theme.colors.clickable;
    primaryColor = theme.colors.white;
  }

  if (outline) {
    return css`
      color: ${primaryColor};

      background-color: transparent;
      border: 1px solid currentColor;
      &[disabled] {
        color: ${theme.colors.grayMed};

        pointer-events: none;
      }
    `;
  }

  return css`
    color: ${secondaryColor};

    background-color: ${primaryColor};
    &[disabled] {
      background: ${theme.colors.grayMed};

      pointer-events: none;
    }
  `;
};

const ButtonSizingStyles: Interpolation<{ theme: Theme } & BaseButtonProps> = [
  ({ bottom, left, right, top }) => css`
    margin-top: ${top ? `${top}rem` : '0'};
    margin-right: ${right ? `${right}rem` : 'auto'};
    margin-bottom: ${bottom ? `${bottom}rem` : '0'};
    margin-left: ${left ? `${left}rem` : 'auto'};
  `,
  ({ inline }) =>
    !!inline &&
    css`
      display: inline-block;
    `,
  ({ withIcon }) => {
    if (withIcon === 'only')
      // 0.2rem all around
      return css`
        width: auto;
        padding: 0.2rem;
      `;
    else if (withIcon === 'sm') {
      // vertical padding becomes 0
      //padding-left becomes 2.1rem
      return css`
        i {
          margin-top: -0.9rem;
          margin-bottom: -0.9rem;
          margin-left: -0.6rem;
        }
      `;
    } else if (withIcon === 'right') {
      // vertical padding becomes 0
      //padding-right becomes 1.7rem
      return css`
        i {
          margin-top: -0.9rem;
          margin-right: -1rem;
          margin-bottom: -0.9rem;
        }
      `;
    } else if (withIcon) {
      // vertical padding becomes 0
      //padding-left becomes 1.7rem
      return css`
        i {
          margin-top: -0.9rem;
          margin-bottom: -0.9rem;
          margin-left: -1rem;
        }
      `;
    }
  },
];

// Emotion forwards every prop to the DOM by default, which means our
// custom design-system props (outline, withIcon, etc.) end up as
// invalid HTML attributes and React warns in the console. List the
// props that are styling-only so they get stripped.
//
// We also filter `as` explicitly: when a styled component has a
// custom shouldForwardProp, emotion uses `as` to swap the rendered
// tag *and* forwards it to the DOM unless we say otherwise — which
// caused `<button as="div">` (a button element with a literal
// as="div" attribute) and produced nested-button hydration errors.
const CUSTOM_BUTTON_PROPS = new Set([
  'as',
  'bottom',
  'buttonStyle',
  'inline',
  'inverted',
  'left',
  'negative',
  'outline',
  'right',
  'top',
  'white',
  'withIcon',
]);
const shouldForwardButtonProp = (prop: string) =>
  !CUSTOM_BUTTON_PROPS.has(prop);

export const ButtonStyled = styled('button', {
  shouldForwardProp: shouldForwardButtonProp,
})<ButtonColorProps & BaseButtonProps>(
  ...BaseButtonStyles,
  ButtonColorStyles,
  ...ButtonSizingStyles
);

interface ChurchButtonProps {
  inverted?: boolean;
  buttonStyle?: ButtonStyle;
}
export const ChurchButtonStyled = styled('button', {
  shouldForwardProp: shouldForwardButtonProp,
})<BaseButtonProps & ChurchButtonProps>(
  BaseButtonStyles,
  ({ theme }) => css`
    margin-right: auto;

    box-shadow: ${theme.shadows.md};
    &[disabled] {
      box-shadow: none;
      opacity: 0.5;

      pointer-events: none;
    }
    &:hover {
      box-shadow: ${theme.shadows.sm};
    }
  `,
  ({ inverted, theme }) =>
    inverted &&
    css`
      color: ${toColor(theme.church.buttonColor)};

      background-color: ${toColor(theme.church.buttonTextColor)};
    `,
  ({ inverted, theme }) =>
    !inverted &&
    css`
      color: ${toColor(theme.church.buttonTextColor)};

      background-color: ${toColor(theme.church.buttonColor)};
    `,
  ...ButtonSizingStyles,
  ({ theme, buttonStyle = theme.church.buttonStyle }) =>
    buttonStyle === 'ROUND' &&
    css`
      border-radius: ${theme.radius.md};
    `,
  ({ theme, buttonStyle = theme.church.buttonStyle }) =>
    buttonStyle === 'SQUARE' &&
    css`
      border-radius: 0rem;
    `,
  ({ theme, buttonStyle = theme.church.buttonStyle }) =>
    buttonStyle === 'PILL' &&
    css`
      border-radius: 2rem;
    `
);

export const SubmitButtonStyled = ({
  children,
  ...props
}: React.PropsWithChildren<
  React.ComponentProps<typeof SubmitButton> &
    ButtonColorProps &
    BaseButtonProps &
    ButtonHTMLAttributes<'button'>
>): ReactElement => {
  return (
    <SubmitButton as={ButtonStyled} css={{ display: 'flex' }} {...props}>
      {children}
    </SubmitButton>
  );
};
export const ChurchSubmitButtonStyled = ({
  children,
  ...props
}: React.PropsWithChildren<
  React.ComponentProps<typeof SubmitButton> &
    ButtonColorProps &
    BaseButtonProps &
    ButtonHTMLAttributes<'button'> &
    ChurchButtonProps & { state?: string }
>): ReactElement => {
  return (
    <SubmitButton as={ChurchButtonStyled} {...props}>
      {children}
    </SubmitButton>
  );
};
